import { useState, useEffect, useRef } from 'react';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import PhoneField from '@Components/MuiWrappers/PhoneField';
import { subscriptionChannelType } from '@Components/Utilities/SandhillsConstants';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import Divider from '@mui/material/Divider';
import { smsType } from '@Components/Utilities/SandhillsConstants';

const Subscriptions = (props) => {

    const emailUpdated = useRef(false);
    const phoneUpdated = useRef(false);
    const intlTelInput = useRef(null);

    const [email, setEmail] = useState(props.Email || '');
    const [emailChecked, setEmailChecked] = useState(false);
    const [emailSuggestion, setEmailSuggestion] = useState('');
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [emailSubscribed, setEmailSubscribed] = useState(false);
    const [emailMessage, setEmailMessage] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [phone, setPhone] = useState(props.SMSPhone || '');
    const [currentSMSPhone, setCurrentSMSPhone] = useState(props.SMSPhone || '');
    const [phoneChecked, setPhoneChecked] = useState(false);
    const [invalidPhone, setInvalidPhone] = useState(false);
    const [phoneSubscribed, setPhoneSubscribed] = useState(false);
    const [phoneMessage, setPhoneMessage] = useState('');
    const [phoneError, setPhoneError] = useState(false);
    const [phoneNonUSAError, setPhoneNonUSAError] = useState(false);
    const [phoneSuggestionError, setPhoneSuggestionError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPhoneSuggestion, setShowPhoneSuggestion] = useState(false);
    const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);
    const [registrationPhone, setRegistrationPhone] = useState(props.RegistrationPhone || '');
    const routes = useRecoilValue(atoms.routes);
    const currentUser = useRecoilValue(atoms.currentUser)
    const currentSite = useRecoilValue(atoms.currentWebsite);

    useEffect(() => {
        if (email || phone) {
            sandhillsRequest({
                url: routes.CheckUserSubscriptionsURL,
                method: 'post',
                data: { 'sEmail': email, 'sPhone': phone },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                setEmailChecked(response[email] ?? false);
                setEmailSubscribed(response[email] ?? false);
                setPhoneChecked(response[phone] ?? false);
                setPhoneSubscribed(response[phone] ?? false);
            });
        }
    }, []);

    const emailChange = (e) => {
        emailUpdated.current = true;
        setEmail(e.target.value);
    };

    const emailCheckboxChange = (e) => {
        setEmailChecked(e.target.checked);
    };

    const emailCheckboxKeydown = (e) => {
        if (e.key === 'Enter') {
            setEmailChecked(emailChecked => !emailChecked);
        }
    };

    const phoneChange = (e) => {
        phoneUpdated.current = true;
        setPhone(e.target.value);
    };

    const phoneCheckboxChange = (e) => {
        setPhoneChecked(e.target.checked);
    };

    const phoneCheckboxKeydown = (e) => {
        if (e.key === 'Enter') {
            setPhoneChecked(phoneChecked => !phoneChecked);
        }
    };

    const submitEmail = () => {
        setLoading(true);
        setEmailError(false);
        setInvalidEmail(false);
        setEmailSuggestion('');
        setEmailSubscribed(false);
        setEmailMessage('');
        sandhillsRequest({
            url: routes.ValidateEmail,
            method: 'post',
            data: { 'sEmail': email },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response.DidYouMean) {
                setEmailSuggestion(response.DidYouMean);
                setLoading(false);
            } else if (response.IsValidEmail) {
                subscribeEmail();
            } else {
                setInvalidEmail(true);
                setLoading(false);
            }
        }).catch(() => {
            subscribeEmail();
        });
    };

    const subscribeEmail = () => {
        sandhillsRequest({
            url: routes.SubscribeUser,
            method: 'post',
            data: {
                user: email,
                subscriptionChannelTypeID: subscriptionChannelType.email,
                subscribe: true,
                webAgreementTypeIDs: props.EmailWebAgreementTypeIDs,
                context: "Email"
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setLoading(false);
            if (response) {
                setEmailSubscribed(true);
                emailUpdated.current = false;
                setEmailMessage(translator.translate('sandhills.tradepubwebsite.mvc.thankyouforyoursubmission'));
            } else {
                setEmailError(true);
            }
        }).catch(() => {
            setLoading(false);
            setEmailError(true);
        });
    };

    const subscribePhone = () => {
        setLoading(true);
        const formattedPhone = intlTelInput.current?.getNumber() ?? phone;
        sandhillsRequest({
            url: routes.SubscribeUser,
            method: 'post',
            data: {
                user: formattedPhone,
                subscriptionChannelTypeID: subscriptionChannelType.textMessage,
                subscribe: true,
                webAgreementTypeIDs: props.TextWebAgreementTypeIDs,
                context: "Text"
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setShowPhoneConfirmation(false);
            if (response) {
                setPhoneSubscribed(true);
                phoneUpdated.current = false;
                setPhoneMessage(translator.translate('sandhills.tradepubwebsite.mvc.thankyouforyoursubmission'));
                if (registrationPhone != formattedPhone) {
                    checkShowPhoneSuggestion(formattedPhone);
                }
                else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
                setPhoneError(true);
            }
        }).catch(() => {
            setLoading(false);
            setPhoneError(true);
        });
    };

    const checkShowPhoneSuggestion = (formattedPhone) => {
        //TODO: Instead of just checking for mobile here, users with bidding rights should need to go through the MFA modal if they choose yes to the phone suggestion
        sandhillsRequest({
            url: routes.CheckPhoneNumberIsMobile,
            method: 'post',
            data: {
                phoneNumber: formattedPhone
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                if (response.CanSendTexts) {
                    setShowPhoneSuggestion(true);
                } else {
                    //Phone number is not mobile, so don't allow them to update registration
                    setShowPhoneSuggestion(false);
                }
            } else {
                //If the API call errors, treat as mobile & allow user to update registration
                setShowPhoneSuggestion(true);
            }
        }).catch((error) => {
            console.log(error);
            //If the API call errors, treat as mobile & allow user to update registration
            setShowPhoneSuggestion(true);
        }).finally(() => {
            setLoading(false);
        });
    };

    const unsubscribeEmail = () => {
        setLoading(true);
        setEmailError(false);
        setEmailMessage('');
        sandhillsRequest({
            url: routes.SubscribeUser,
            method: 'post',
            data: {
                user: email,
                subscriptionChannelTypeID: subscriptionChannelType.email,
                subscribe: false
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setLoading(false);
            if (response) {
                setEmail('');
                setEmailChecked(false);
                setEmailSubscribed(false);
                emailUpdated.current = false;
                setEmailMessage(translator.translate('sandhills.tradepubwebsite.mvc.youhavebeenunsubscribed'));
            } else {
                setEmailError(true);
            }
        }).catch(() => {
            setLoading(false);
            setEmailError(true);
        });
    };

    const unsubscribePhone = () => {
        setLoading(true);
        setPhoneError(false);
        setInvalidPhone(false);
        setPhoneMessage('');
        const formattedPhone = intlTelInput.current?.getNumber() ?? phone;
        sandhillsRequest({
            url: routes.SubscribeUser,
            method: 'post',
            data: {
                user: formattedPhone,
                subscriptionChannelTypeID: subscriptionChannelType.textMessage,
                subscribe: false
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setLoading(false);
            if (response) {
                setPhone('');
                setPhoneChecked(false);
                setPhoneSubscribed(false);
                setShowPhoneSuggestion(false);
                phoneUpdated.current = false;
                setPhoneMessage(translator.translate('sandhills.tradepubwebsite.mvc.youhavebeenunsubscribed'));
            } else {
                setPhoneError(true);
            }
        }).catch(() => {
            setLoading(false);
            setPhoneError(true);
        });
    };

    const updateRegistrationPhone = () => {
        setLoading(true);
        setPhoneSuggestionError(false);
        const formattedPhone = intlTelInput.current?.getNumber() ?? phone;
        sandhillsRequest({
            url: routes.UpdateRegistrationPhone,
            method: 'post',
            data: {
                phone: formattedPhone,
                centralUserAuthID: props.CentralUserAuthID
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setLoading(false);
            if (response) {
                setShowPhoneSuggestion(false);
                setRegistrationPhone(formattedPhone);
            } else {
                setPhoneSuggestionError(true);
            }
        }).catch(() => {
            setLoading(false);
            setPhoneSuggestionError(true);
        });
    };

    const hidePhoneSuggestion = () => {
        setShowPhoneSuggestion(false);
    };

    const hidePhoneConfirmation = () => {
        setShowPhoneConfirmation(false);
    };

    const submitPhone = () => {
        setPhoneError(false);
        setInvalidPhone(false);
        setPhoneSubscribed(false);
        setPhoneNonUSAError(false);
        setPhoneMessage('');
        if (intlTelInput.current?.isValidNumber() == false) {
            setInvalidPhone(true);
        } else if (intlTelInput.current?.getSelectedCountryData().name.toLowerCase() !== 'united states') {
            setPhoneNonUSAError(true);
            setPhoneChecked(false);
        } else if (currentUser.IsAuthenticated) {
            const formattedPhone = intlTelInput.current?.getNumber() ?? phone;
            sandhillsRequest({
                url: routes.CheckShowSMSPhoneUpdateConfirmation,
                method: 'post',
                data: {
                    smsPhone: formattedPhone,
                    smsSignUpTypeID: smsType.marketing
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response.showConfirmation) {
                    setLoading(false);
                    setCurrentSMSPhone(response.oldSMSPhone);
                    setShowPhoneConfirmation(true);
                } else {
                    subscribePhone();
                }
            }).catch((error) => {
                console.log(error);
                setLoading(false);
                setPhoneError(true);
            });
        } else {
            subscribePhone();
        }
    };

    const selectSuggestedEmail = (email) => {
        setEmail(email);
        setEmailSuggestion('');
    };

    const getTitleText = () => {
        if (props.ShowEmail) {
            if (props.ShowTextMessage) {
                if (currentUser.IsAuthenticated) {
                    return <TranslationText tKey='sandhills.tradesites.mvc.communicationpreferences' />
                } else if (props.Unsubscribe) {
                    return <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                } else {
                    return <span>{currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '') + " "  + translator.translate('sandhills.subscriptioncenter')}</span>   
                }
            }
            if (currentUser.IsAuthenticated) {
                return <TranslationText tKey='sandhills.updatemy' replaceValues={[translator.translate('sandhills.emailsettings')]} />
            } else {
                if (props.Unsubscribe) {
                    return <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                } else {
                    return <span>{currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '') + " " + translator.translate('sandhills.subscriptioncenter')}</span>
                }
            }
        } else if (props.ShowTextMessage) {
            if (currentUser.IsAuthenticated) {
                return <TranslationText tKey='sandhills.updatemy' replaceValues={[translator.translate('sandhills.textsettings')]} />
            } else {
                if (props.Unsubscribe) {
                    return <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                } else {
                    return <span>{currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '') + " " + translator.translate('sandhills.subscriptioncenter')}</span>
                }
            }
        }
        return '';
    };

    const getEmailInputProps = () => {
        return emailSubscribed && currentUser.IsAuthenticated ? {
            endAdornment: (
                <InputAdornment position='end' sx={{ display: { xs: 'none', md: 'flex' } }}>
                    <Button variant='outlined' onClick={unsubscribeEmail}>
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                    </Button>
                </InputAdornment>
            )
        } : undefined;
    };

    const getPhoneInputProps = () => {
        return phoneSubscribed && currentUser.IsAuthenticated ? {
            endAdornment: (
                <InputAdornment position='end' sx={{ display: { xs: 'none', md: 'flex' } }}>
                    <Button variant='outlined' onClick={unsubscribePhone}>
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                    </Button>
                </InputAdornment>
            )
        } : undefined;
    };

    const getEmailErrorText = () => {
        if (invalidEmail) {
            return translator.translate('sandhills.registration.invalidemail');
        } else if (emailSuggestion) {
            return <>{translator.translate('sandhills.registration.emailsuggesttext').replace('{0}?', ' ')}<Link onClick={() => selectSuggestedEmail(emailSuggestion)}>{emailSuggestion}</Link>?</>;
        } else if (emailError) {
            return translator.translate('sandhills.anerroroccuredpleasetryagain');
        }
        return undefined;
    };

    const getPhoneErrorText = () => {
        if (invalidPhone) {
            return translator.translate('sandhills.invalidphone');
        } else if (phoneError) {
            return translator.translate('sandhills.anerroroccuredpleasetryagain');
        } else if (phoneNonUSAError) {
            return translator.translate('sandhills.sorrythisisonlyforusphone');
        }

        return undefined;
    };

    const emailButtonDisabled = () => {
        return (loading || !email || !emailChecked || (emailSubscribed && !emailUpdated.current));
    };

    const UnsubscribeEmailButtonDisabled = () => {
        return (loading || !email || !emailUpdated.current);
    };

    const phoneButtonDisabled = () => {
        return (loading || showPhoneConfirmation || !phone || !phoneChecked || (phoneSubscribed && !phoneUpdated.current));
    };

    const UnsubscribePhoneButtonDisabled = () => {
        return (loading || !phone || !phoneUpdated.current);
    };

    return (
        <>
            <div style={{ width: '100%', maxWidth: !currentUser.IsAuthenticated ? '1140px' : '', margin: !currentUser.IsAuthenticated ? 'auto' : '', padding: !currentUser.IsAuthenticated ? '20px' : '' }}>
                {!currentUser.IsAuthenticated &&
                    <div className='info-content-header'>
                        <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={props.BreadCrumbText} />
                    </div>}
                <div className='notification-header'>
                    <h3 className='mobile-hide'>
                        {getTitleText()}
                    </h3>
                    {currentUser.IsAuthenticated && <a className='account-button title' href={props.WatchListUrl}>{translator.translate('sandhills.tradesites.mvc.viewmywatchlist')}</a>}
                </div>
                {!currentUser.IsAuthenticated &&
                    <Divider sx={{ marginBottom: '20px' }}></Divider>}
                {!currentUser.IsAuthenticated && !props.Unsubscribe &&
                    <Typography sx={{ marginBottom: '20px' }}>
                        <Typography dangerouslySetInnerHTML={{ __html: props.UnsubscribePageLink }} />
                    </Typography>}
                {props.Unsubscribe &&
                    <Typography sx={{ marginBottom: '20px' }}>
                        <Typography sx={{ marginBottom: '20px' }} dangerouslySetInnerHTML={{ __html: props.SubscribePageLink }} />
                    </Typography>}
                {props.ShowEmail &&
                    <Paper className='grey-box' sx={{ marginBottom: '15px', padding: { xs: '10px', md: '30px' } }} elevation={2}>
                        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                            <Grid item xs={12}>
                                <h4>
                                    <TranslationText tKey='sandhills.tradesites.mvc.marketingemailalerts' />
                                </h4>
                            </Grid>
                            {!props.Unsubscribe &&
                                <>
                                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <label htmlFor="emailCheckbox"><p hidden><TranslationText tKey='sandhills.tradesites.mvc.marketingemailalerts' /></p></label>
                                            <Checkbox
                                            id='emailCheckbox'
                                            size='small'
                                            checked={emailChecked}
                                            onChange={emailCheckboxChange}
                                            onKeyDown={emailCheckboxKeydown}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                padding: '0px'
                                            }}
                                            inputProps={{
                                                'id': 'emailCheckbox'
                                            }}
                                            />
                                        
                                        <Typography sx={{ fontSize: '14px', paddingLeft: '5px' }} dangerouslySetInnerHTML={{ __html: props.EmailSubscriptionPolicy }} />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', paddingLeft: '5px' }} dangerouslySetInnerHTML={{ __html: props.EmailTermsAndPolicyAgreement }} />
                                    </Grid>
                                </>
                            }
                            <Grid item xs={12} md={9}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    id='Email'
                                    name='Email'
                                    value={email}
                                    onChange={emailChange}
                                    label={translator.translate('sandhills.email')}
                                    placeholder={translator.translate('sandhills.enteryouremailaddress')}
                                    error={getEmailErrorText() ? true : false}
                                    InputProps={getEmailInputProps()}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {props.Unsubscribe ?
                                    <Button variant='contained' sx={{ width: '100%' }} disabled={UnsubscribeEmailButtonDisabled()} onClick={unsubscribeEmail}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                                    </Button>
                                    :
                                    <Button variant='contained' sx={{ width: '100%' }} disabled={emailButtonDisabled()} onClick={submitEmail}>
                                        <TranslationText tKey='sandhills.savechanges' />
                                    </Button>}
                            </Grid>
                            {!props.Unsubscribe &&
                                <Grid item xs={12}>
                                    <Typography dangerouslySetInnerHTML={{ __html: props.DigitalEditionSubscribeLink }} />
                                </Grid>}
                            {emailSubscribed &&
                                <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' } }}>
                                    <Button variant='outlined' sx={{ width: '100%' }} onClick={unsubscribeEmail}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                                    </Button>
                                </Grid>}
                            {emailMessage &&
                                <Grid item xs={12}>
                                    <Typography color='success.main'>
                                        {emailMessage}
                                    </Typography>
                                </Grid>}
                            {getEmailErrorText() &&
                                <Grid item xs={12}>
                                    <Typography color='error.main'>
                                        {getEmailErrorText()}
                                    </Typography>
                                </Grid>}
                        </Grid>
                    </Paper>}
                {props.ShowTextMessage &&
                    <Paper className='grey-box' sx={{ marginBottom: '15px', padding: { xs: '10px', md: '30px' } }} elevation={2}>
                        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                            <Grid item xs={12}>
                                <h4>
                                    <TranslationText tKey='sandhills.tradesites.mvc.marketingtextalerts' />
                                </h4>
                            </Grid>
                            {!props.Unsubscribe &&
                                <>
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            <TranslationText tKey='sandhills.stayuptodateandreceivetextmessages' />
                                        </Typography>
                                    </Grid>

                                <Grid item xs={12} sx={{ display: 'flex' }}>
                                        <Checkbox
                                            id="textCheckbox"
                                            size='small'
                                            checked={phoneChecked}
                                            onChange={phoneCheckboxChange}
                                            onKeyDown={phoneCheckboxKeydown}
                                            disabled={showPhoneConfirmation}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                padding: '0px'
                                        }}
                                        inputProps={{
                                            'aria-label': <TranslationText tKey='sandhills.tradesites.mvc.marketingtextalerts' />,
                                        }}
                                        />                  
                                    <Typography sx={{ fontSize: '12px', paddingLeft: '5px' }} dangerouslySetInnerHTML={{ __html: props.TextSubscriptionPolicy }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                    <Typography sx={{ fontSize: '12px', paddingLeft: '25px' }} dangerouslySetInnerHTML={{ __html: props.TextTermsAndPolicyAgreement }} />
                                    </Grid>
                                </>}
                            <Grid item xs={12} md={9}>
                                <PhoneField
                                    id='Phone'
                                    name='Phone'
                                    size='small'
                                    value={phone}
                                    onChange={phoneChange}
                                    label={translator.translate('sandhills.phone')}
                                    error={getPhoneErrorText() ? true : false}
                                    intlTelInput={input => intlTelInput.current = input}
                                    fullWidth
                                    InputProps={getPhoneInputProps()}
                                    disabled={showPhoneConfirmation}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {props.Unsubscribe ?
                                    <Button variant='contained' sx={{ width: '100%' }} disabled={UnsubscribePhoneButtonDisabled()} onClick={unsubscribePhone}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                                    </Button>
                                    :
                                    <Button variant='contained' sx={{ width: '100%' }} disabled={phoneButtonDisabled()} onClick={submitPhone}>
                                        <TranslationText tKey='sandhills.signup' />
                                    </Button>}
                            </Grid>
                            {phoneSubscribed &&
                                <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' } }}>
                                    <Button variant='outlined' sx={{ width: '100%' }} onClick={unsubscribePhone}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.unsubscribe' />
                                    </Button>
                                </Grid>}
                            {phoneMessage &&
                                <Grid item xs={12}>
                                    <Typography color='success.main'>
                                        {phoneMessage}
                                    </Typography>
                                </Grid>}
                            {getPhoneErrorText() &&
                                <Grid item xs={12}>
                                    <Typography color='error.main'>
                                        {getPhoneErrorText()}
                                    </Typography>
                                </Grid>}
                            {showPhoneSuggestion && currentUser.IsAuthenticated &&
                                <>
                                    <Grid item xs={12}>
                                        <Typography color='success.main' sx={{ fontSize: '14px' }}>
                                            <TranslationText tKey='sandhills.updateregistrationphone' />
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button variant='contained' sx={{ width: '100%' }} onClick={updateRegistrationPhone}>
                                            <TranslationText tKey='sandhills.yes' />
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button variant='contained' sx={{ width: '100%' }} onClick={hidePhoneSuggestion}>
                                            <TranslationText tKey='sandhills.no' />
                                        </Button>
                                    </Grid>
                                </>}
                            {showPhoneConfirmation && currentUser.IsAuthenticated &&
                                <>
                                    <Grid item xs={12}>
                                    <Typography color='error.main' sx={{ fontSize: '12px' }} dangerouslySetInnerHTML={{ __html: props.SMSPhoneUpdateConfirmationText }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography color='error.main' sx={{ fontSize: '12px' }}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.oldinfo' /> : { currentSMSPhone }
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography color='error.main' sx={{ fontSize: '12px' }}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.newinfo' /> : { intlTelInput.current?.getNumber() }
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Button variant='contained' sx={{ width: '100%' }} onClick={subscribePhone}>
                                        <TranslationText tKey='sandhills.ok' />
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Button variant='contained' sx={{ width: '100%' }} onClick={hidePhoneConfirmation}>
                                        <TranslationText tKey='sandhills.cancel' />
                                    </Button>
                                </Grid>
                                </>}
                            {phoneSuggestionError &&
                                <Grid item xs={12}>
                                    <Typography color='error.main'>
                                        <TranslationText tKey='sandhills.anerroroccuredpleasetryagain' />
                                    </Typography>
                                </Grid>}
                        </Grid>
                    </Paper>}
                <div>
                    {props.Unsubscribe &&
                        <Typography sx={{ marginBottom: '20px' }} dangerouslySetInnerHTML={{ __html: props.DigitalEditionUnsubscribeLink }} />}
                    {props.PrivacyPolicy &&
                        <Typography sx={{ fontSize: '12px' }} dangerouslySetInnerHTML={{ __html: props.PrivacyPolicy }} />}
                </div>
                {loading &&
                    <LoadingRing backdrop />}
            </div>
        </>
    );
};

export default Subscriptions;